import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import {
  ArrowLeft,
  Check,
  Eye,
  EyeOff,
  Lock,
  LogOut,
  ShieldCheck
} from 'lucide-react'
import travelAgentService from '../services/travelAgentService'
import LoadingSpinner from '../components/LoadingSpinner'
import AxonallLogo from '../components/AxonallLogo'

const HERO_IMAGE =
  'https://d1su8gdu4l1hio.cloudfront.net/origin+mvp+assets/IDK-REQ/atlantis-the-palmjpg-1b19e1d1-b721-471b-b737-a08aea6cf0f5.jpg'

const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/

function Requirement({ met, label }) {
  return (
    <div
      className={`flex items-center gap-1.5 text-[11px] leading-tight font-poppinsRegular ${
        met ? 'text-emerald-700' : 'text-neutral-500'
      }`}
    >
      <span
        className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border ${
          met ? 'border-emerald-600 bg-emerald-50' : 'border-neutral-300 bg-white'
        }`}
      >
        {met && <Check className="h-2 w-2" aria-hidden />}
      </span>
      {label}
    </div>
  )
}

const ChangePasswordRequiredPage = () => {
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()

  const user = travelAgentService.getCurrentUser()

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm({ mode: 'onBlur' })

  const newPassword = watch('newPassword') || ''
  const confirmPassword = watch('confirmPassword') || ''

  const checks = useMemo(
    () => ({
      length: newPassword.length >= 8,
      upper: /[A-Z]/.test(newPassword),
      lower: /[a-z]/.test(newPassword),
      number: /\d/.test(newPassword),
      special: /[@$!%*?&]/.test(newPassword)
    }),
    [newPassword]
  )

  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword

  const onSubmit = async (data) => {
    if (!PASSWORD_PATTERN.test(data.newPassword)) {
      toast.error('Please meet all password requirements')
      return
    }
    if (data.newPassword !== data.confirmPassword) {
      toast.error('Passwords do not match')
      return
    }
    if (data.newPassword === data.currentPassword) {
      toast.error('New password must be different from current password')
      return
    }

    setIsLoading(true)
    try {
      const response = await travelAgentService.changePassword(
        data.currentPassword,
        data.newPassword
      )
      toast.success(response.message || 'Password updated')
      navigate(travelAgentService.getPostLoginPath())
    } catch (error) {
      toast.error(error.message || 'Failed to update password')
    } finally {
      setIsLoading(false)
    }
  }

  const handleLogout = async () => {
    await travelAgentService.logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className="h-[100dvh] max-h-[100dvh] flex flex-col lg:flex-row overflow-hidden bg-white antialiased font-poppinsRegular text-neutral-800">
      {/* Brand panel */}
      <div className="hidden lg:flex lg:w-[46%] xl:w-1/2 relative min-h-0 overflow-hidden shrink-0">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-105"
          style={{ backgroundImage: `url('${HERO_IMAGE}')` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/75 via-black/50 to-black/80" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-black/55" />

        <div className="absolute top-6 left-6 xl:top-8 xl:left-8 z-20">
          <button
            type="button"
            onClick={handleLogout}
            className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-black/25 px-4 py-2.5 text-[10px] font-poppinsSemiBold uppercase tracking-[0.2em] text-white backdrop-blur-md transition-colors hover:bg-black/40 hover:border-white/30"
          >
            <ArrowLeft className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
            Sign out
          </button>
        </div>

        <div className="relative z-10 flex flex-1 flex-col justify-center px-10 xl:px-16 2xl:px-20 py-12">
          <div className="max-w-md rounded-3xl border border-white/15 bg-black/35 p-9 xl:p-10 shadow-2xl shadow-black/40 backdrop-blur-2xl">
            <div className="mb-6 border-b border-white/10 pb-6 flex items-center justify-between">
              <AxonallLogo color="text-white" size="text-3xl xl:text-4xl" />
              <span className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5">
                <ShieldCheck className="h-5 w-5 text-white/80" aria-hidden />
              </span>
            </div>
            <p className="text-[10px] font-poppinsSemiBold uppercase tracking-[0.35em] text-white/55 mb-3">
              Studio | Secure access
            </p>
            <p className="font-AXONALLPrimeRegular text-2xl xl:text-3xl font-medium leading-snug tracking-tight text-white">
              Secure your account
            </p>
            <p className="mt-4 text-sm font-poppinsLight leading-relaxed text-white/80">
              You signed in with a temporary password. Set a new password before you continue to
              Axonall Studio.
            </p>
            <ul className="mt-6 space-y-3 border-t border-white/10 pt-6 text-sm font-poppinsLight text-white/70">
              <li className="flex gap-3">
                <span className="font-poppinsSemiBold text-white/35 tabular-nums">01</span>
                <span>Enter the temporary password from your welcome email.</span>
              </li>
              <li className="flex gap-3">
                <span className="font-poppinsSemiBold text-white/35 tabular-nums">02</span>
                <span>Create a new password that meets the security requirements.</span>
              </li>
              <li className="flex gap-3">
                <span className="font-poppinsSemiBold text-white/35 tabular-nums">03</span>
                <span>You will be taken straight to your Studio dashboard.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Form column */}
      <div className="relative flex h-[100dvh] min-h-0 flex-1 flex-col overflow-hidden px-4 sm:px-8 lg:bg-gradient-to-b lg:from-neutral-50 lg:via-white lg:to-neutral-100 lg:px-12 xl:px-16">
        <div className="pointer-events-none absolute inset-0 z-0 lg:hidden" aria-hidden>
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-105"
            style={{ backgroundImage: `url('${HERO_IMAGE}')` }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/55 via-black/35 to-black/65" />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-100 via-white/88 to-white/72" />
        </div>

        <div
          className="pointer-events-none absolute -right-24 top-[12%] z-0 h-72 w-72 rounded-full bg-neutral-900/[0.04] blur-3xl max-lg:opacity-40"
          aria-hidden
        />

        <div className="relative z-10 flex min-h-0 flex-1 flex-col">
          {/* Mobile header */}
          <div className="mb-4 flex shrink-0 items-center justify-between pt-6 lg:hidden">
            <AxonallLogo size="text-xl" color="text-neutral-900" />
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-[10px] font-poppinsSemiBold uppercase tracking-[0.18em] text-neutral-700 shadow-sm"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign out
            </button>
          </div>

          <div className="flex min-h-0 flex-1 flex-col justify-center py-4 lg:py-6">
            <div className="mx-auto w-full max-w-md">
              <div className="mb-5 text-center lg:text-left">
                <h1 className="font-AXONALLPrimeRegular text-2xl sm:text-[1.65rem] font-medium tracking-tight text-neutral-900">
                  Set a new password
                </h1>
                <p className="mt-1.5 text-sm font-poppinsRegular text-neutral-500">
                  For security, change your temporary password before continuing.
                </p>
                {user?.email && (
                  <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-neutral-50 px-3.5 py-1.5">
                    <Lock className="h-3.5 w-3.5 text-neutral-400" />
                    <span className="text-xs font-poppinsMedium text-neutral-700">
                      {user.email}
                    </span>
                  </div>
                )}
              </div>

              <div className="rounded-[1.5rem] border border-neutral-200/90 bg-white/95 p-6 sm:p-7 shadow-[0_20px_70px_rgba(15,23,42,0.08)] backdrop-blur-sm">
                <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
                  <PasswordInput
                    id="currentPassword"
                    label="Current (temporary) password"
                    show={showCurrent}
                    onToggle={() => setShowCurrent((s) => !s)}
                    placeholder="From your welcome email"
                    autoComplete="current-password"
                    error={errors.currentPassword?.message}
                    registerProps={register('currentPassword', {
                      required: 'Current password is required'
                    })}
                  />

                  <PasswordInput
                    id="newPassword"
                    label="New password"
                    show={showNew}
                    onToggle={() => setShowNew((s) => !s)}
                    placeholder="Create a strong password"
                    autoComplete="new-password"
                    error={errors.newPassword?.message}
                    registerProps={register('newPassword', {
                      required: 'New password is required',
                      validate: (v) =>
                        PASSWORD_PATTERN.test(v) ||
                        'Must include upper, lower, number, and special (@$!%*?&)'
                    })}
                  />

                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 rounded-lg border border-neutral-100 bg-neutral-50/80 px-3 py-2 sm:grid-cols-3">
                    <Requirement met={checks.length} label="8+ characters" />
                    <Requirement met={checks.upper} label="Uppercase" />
                    <Requirement met={checks.lower} label="Lowercase" />
                    <Requirement met={checks.number} label="Number" />
                    <Requirement met={checks.special} label="Special (@$!%*?&)" />
                  </div>

                  <PasswordInput
                    id="confirmPassword"
                    label="Confirm new password"
                    show={showConfirm}
                    onToggle={() => setShowConfirm((s) => !s)}
                    placeholder="Re-enter the new password"
                    autoComplete="new-password"
                    error={
                      errors.confirmPassword?.message ||
                      (confirmPassword && !passwordsMatch ? 'Passwords do not match' : undefined)
                    }
                    registerProps={register('confirmPassword', {
                      required: 'Please confirm your password',
                      validate: (v) => v === newPassword || 'Passwords do not match'
                    })}
                  />

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="typography-button flex w-full items-center justify-center gap-2 rounded-xl bg-neutral-950 py-3 text-xs font-medium tracking-[0.2em] text-white shadow-lg shadow-neutral-900/15 transition-all hover:bg-black hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isLoading ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        Updating…
                      </>
                    ) : (
                      'Update password'
                    )}
                  </button>
                </form>
              </div>

              <footer className="mt-5 flex flex-col items-center justify-between gap-2 text-center sm:flex-row lg:text-left">
                <p className="text-[11px] font-poppinsRegular text-neutral-400">
                  © 2026 Axonall Luxury Travel
                </p>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="hidden lg:inline-flex items-center gap-1.5 text-xs font-poppinsMedium text-neutral-500 hover:text-neutral-800"
                >
                  <LogOut className="h-3.5 w-3.5" />
                  Sign out
                </button>
              </footer>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function PasswordInput({
  id,
  label,
  show,
  onToggle,
  placeholder,
  autoComplete,
  error,
  registerProps
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="mb-2 block text-[11px] font-poppinsSemiBold uppercase tracking-wider text-neutral-600"
      >
        {label}
      </label>
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
          <Lock className="h-[18px] w-[18px] text-neutral-400" aria-hidden />
        </div>
        <input
          id={id}
          type={show ? 'text' : 'password'}
          autoComplete={autoComplete}
          placeholder={placeholder}
          className="input-field h-11 border-neutral-200 bg-white pl-11 pr-11 text-[15px] focus:border-neutral-900/20 focus:ring-neutral-900/25"
          {...registerProps}
        />
        <button
          type="button"
          onClick={onToggle}
          aria-label={show ? 'Hide password' : 'Show password'}
          className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-neutral-400 transition-colors hover:text-neutral-700"
        >
          {show ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
        </button>
      </div>
      {error && <p className="error-text">{error}</p>}
    </div>
  )
}

export default ChangePasswordRequiredPage
